import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Stack, Button, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LeafIcon from '@mui/icons-material/Spa';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const STATS = [
  { value: '30%', label: '탄소 감축' },
  { value: '98%', label: '고객 만족도' },
  { value: '500+', label: '도입 기업' },
];

function SecondEventView() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: '#0a1628',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* 배경 그라데이션 */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 70% 50% at 80% 50%, rgba(0,137,123,0.2) 0%, transparent 70%), radial-gradient(ellipse 50% 40% at 10% 80%, rgba(0,188,212,0.1) 0%, transparent 60%)',
          pointerEvents: 'none',
        }}
      />

      {/* 격자 패턴 */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }}
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems="center"
        justifyContent="center"
        spacing={{ xs: 4, md: 8 }}
        sx={{ position: 'relative', zIndex: 1, px: { xs: 3, md: 8 }, maxWidth: '1100px', width: '100%' }}
      >
        {/* 왼쪽: 대시보드 시각화 */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
        >
          <Box
            sx={{
              width: { xs: '220px', md: '320px' },
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              p: 3,
              backdropFilter: 'blur(10px)',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
              <DashboardIcon sx={{ color: '#26c6da', fontSize: 20 }} />
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '13px', color: '#fff' }}>
                ESG 대시보드
              </Typography>
            </Stack>

            {/* 가짜 차트 바 */}
            {[
              { label: '탄소 절감', pct: 78, color: '#26c6da' },
              { label: '에너지 절약', pct: 62, color: '#4caf50' },
              { label: '물 절약', pct: 85, color: '#29b6f6' },
            ].map((item, i) => (
              <Box key={item.label} sx={{ mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontFamily: 'NotoSansKR', fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ fontFamily: 'NotoSansKR', fontSize: '11px', color: item.color, fontWeight: 700 }}>
                    {item.pct}%
                  </Typography>
                </Stack>
                <Box sx={{ height: '6px', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${item.pct}%` }}
                    transition={{ duration: 1, delay: 0.5 + i * 0.15 }}
                    style={{ height: '100%', backgroundColor: item.color, borderRadius: '3px' }}
                  />
                </Box>
              </Box>
            ))}

            {/* 하단 스탯 */}
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
              {STATS.map((s) => (
                <Box key={s.label} sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: '18px', color: '#ffffff' }}>
                    {s.value}
                  </Typography>
                  <Typography sx={{ fontFamily: 'NotoSansKR', fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </motion.div>

        {/* 오른쪽: 텍스트 */}
        <Box sx={{ flex: 1, maxWidth: '480px' }}>
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <Chip
              label="다끌레어 플랫폼"
              size="small"
              sx={{
                fontFamily: 'NotoSansKR',
                fontWeight: 700,
                fontSize: '11px',
                letterSpacing: '2px',
                backgroundColor: 'rgba(0,188,212,0.15)',
                color: '#26c6da',
                border: '1px solid rgba(0,188,212,0.3)',
                mb: 2.5,
              }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
          >
            <Typography
              sx={{
                fontFamily: 'NotoSansKR',
                fontWeight: 900,
                fontSize: { xs: '28px', sm: '36px', md: '44px' },
                color: '#ffffff',
                lineHeight: 1.2,
                letterSpacing: '-1px',
                mb: 2,
              }}
            >
              ESG 경영을
              <br />
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(90deg, #26c6da, #00bcd4)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                데이터로 증명하세요
              </Box>
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <Typography
              sx={{
                fontFamily: 'NotoSansKR',
                fontWeight: 300,
                fontSize: { xs: '14px', md: '16px' },
                color: 'rgba(255,255,255,0.5)',
                lineHeight: 1.8,
                mb: 4,
              }}
            >
              탄소 배출량, 에너지 사용량, 용수 절감량을 실시간으로 추적하고
              ESG 리포트를 자동으로 생성합니다.
            </Typography>

            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/services/daclair')}
              sx={{
                fontFamily: 'NotoSansKR',
                fontWeight: 700,
                fontSize: '14px',
                px: 3.5,
                py: 1.4,
                borderRadius: '50px',
                backgroundColor: '#00bcd4',
                color: '#ffffff',
                boxShadow: '0 0 25px rgba(0,188,212,0.35)',
                '&:hover': {
                  backgroundColor: '#26c6da',
                  boxShadow: '0 0 35px rgba(0,188,212,0.5)',
                },
              }}
            >
              플랫폼 알아보기
            </Button>
          </motion.div>
        </Box>
      </Stack>
    </Box>
  );
}

export default SecondEventView;
